import { supabase } from './authService/supabase';

export interface StreamApiConfig {
    baseURL: string;
    headers?: Record<string, string>;
}

export interface StreamPostOptions {
    url: string;
    body?: any;
    headers?: Record<string, string>;
    onMessage: (data: any) => void;
    onError?: (error: any) => void;
    signal?: AbortSignal;
}

export interface StreamGetOptions {
    url: string;
    params?: any;
    headers?: Record<string, string>;
    onMessage: (data: any) => void;
    onError?: (error: any) => void;
    signal?: AbortSignal;
}

type Middleware = (request: Request, next: () => Promise<Response>) => Promise<Response>;

export class StreamApi {
    private baseURL: string;
    private headers: Record<string, string>;
    private middlewares: Middleware[] = [];

    constructor(config: StreamApiConfig) {
        this.baseURL = config.baseURL;
        this.headers = {
            'Content-Type': 'application/json',
            ...(config.headers || {})
        };

        // 添加认证中间件
        this.use(async (request, next) => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.access_token) {
                request.headers.set('Authorization', `Bearer ${session.access_token}`);
            }
            return next();
        });
    }

    /**
     * 添加中间件
     * @param middleware 中间件函数
     */
    public use(middleware: Middleware): void {
        this.middlewares.push(middleware);
    }



    /**
     * 执行请求
     * @param url 请求URL
     * @param options 请求选项
     */
    private async executeRequest(url: string, options: RequestInit): Promise<Response> {
        const request = new Request(`${this.baseURL}${url}`, options);

        const executeMiddleware = async (index: number): Promise<Response> => {
            if (index >= this.middlewares.length) {
                return fetch(request);
            }

            const middleware = this.middlewares[index];
            return middleware(request, () => executeMiddleware(index + 1));
        };

        return executeMiddleware(0);
    }

    /**
     * 发起流式 POST 请求
     * @param options StreamPostOptions
     */
    public async post(options: StreamPostOptions): Promise<void> {
        const {
            url, body, headers, onMessage, onError, signal 
        } = options;
        
        try {
            const requestHeaders = {
                ...this.headers,
                ...(headers || {})
            };

            if (body instanceof FormData) {
                delete requestHeaders['Content-Type'];
            }

            const response = await this.executeRequest(url, {
                method: 'POST',
                headers: requestHeaders,
                body: body instanceof FormData ? body : JSON.stringify(body),
                signal
            });

            if (!response.ok || !response.body) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            await this.handleStreamResponse(response, onMessage, signal);
        } catch (error) {
            if (signal?.aborted) {
                // 请求被中止，不调用 onError
                return;
            }
            if (onError) onError(error);
            else throw error;
        }
    }

    /**
     * 发起流式 GET 请求
     * @param options StreamGetOptions
     */
    public async get(options: StreamGetOptions): Promise<void> {
        const {
            url, params, headers, onMessage, onError, signal 
        } = options;
        
        try {
            const requestHeaders = {
                ...this.headers,
                ...(headers || {})
            };

            let fullUrl = url;
            if (params) {
                const searchParams = new URLSearchParams(params);
                fullUrl += `?${searchParams.toString()}`;
            }

            const response = await this.executeRequest(fullUrl, {
                method: 'GET',
                headers: requestHeaders,
                signal
            });

            if (!response.ok || !response.body) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            await this.handleStreamResponse(response, onMessage, signal);
        } catch (error) {
            if (signal?.aborted) {
                // 请求被中止，不调用 onError
                return;
            }
            if (onError) onError(error);
            else throw error;
        }
    }

    /**
     * 处理流式响应
     * @param response Response对象
     * @param onMessage 消息回调
     * @param signal AbortSignal
     */
    private async handleStreamResponse(
        response: Response, 
        onMessage: (data: any) => void, 
        signal?: AbortSignal
    ): Promise<void> {
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let done = false;
        let buffer = '';
        let currentEvent = '';
        let currentData = '';

        while (!done) {
            // 检查是否被中止
            if (signal?.aborted) {
                reader.cancel();
                return;
            }

            const { value, done: readerDone } = await reader.read();
            if (value) {
                buffer += decoder.decode(value, { stream: !readerDone });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';
                
                for (const line of lines) {
                    if (line.trim() === '') {
                        // 空行表示一个完整的SSE消息结束
                        if (currentEvent && currentData) {
                            try {
                                const data = JSON.parse(currentData);
                                data.event_type = currentEvent;
                                onMessage(data);
                            } catch (e) {
                                // 忽略解析失败的数据
                                console.warn('Failed to parse SSE data:', currentData, e);
                            }
                        }
                        currentEvent = '';
                        currentData = '';
                    } else if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    } else if (line.startsWith('data: ')) {
                        currentData = line.slice(6).trim();
                    }
                }
            }
            
            // 更新循环条件
            if (readerDone) {
                done = true;
            }
        }

        // 处理最后剩余的数据
        if (currentEvent && currentData) {
            try {
                const data = JSON.parse(currentData);
                data.event_type = currentEvent;
                onMessage(data);
            } catch (e) {
                console.warn('Failed to parse final SSE data:', currentData, e);
            }
        }

    }
}